import { type ReactNode, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../utils';

interface TooltipProps {
  content: string;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

type Side = 'top' | 'bottom' | 'left' | 'right';

const OFFSET        = 7;   // px gap between trigger and tooltip
const EST_HEIGHT    = 28;  // px — estimated tooltip height for flip check
const EST_WIDTH     = 220; // px — estimated max tooltip width for clamp
const VIEWPORT_PAD  = 6;   // px — min distance from viewport edge

/** Opposite side for auto-flip */
const _FLIP: Record<Side, Side> = {
  top: 'bottom', bottom: 'top', left: 'right', right: 'left',
};

/** CSS transform for each side so the tooltip aligns with its anchor */
const TRANSFORM: Record<Side, string> = {
  top:    'translate(-50%, -100%)',
  bottom: 'translate(-50%, 0)',
  left:   'translate(-100%, -50%)',
  right:  'translate(0, -50%)',
};

interface Position { top: number; left: number; resolvedSide: Side }

function calcPosition(rect: DOMRect, preferredSide: Side): Position {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  /* Decide whether to flip the preferred side */
  let side = preferredSide;
  if (preferredSide === 'top'    && rect.top    - EST_HEIGHT - OFFSET < VIEWPORT_PAD) side = 'bottom';
  if (preferredSide === 'bottom' && rect.bottom + EST_HEIGHT + OFFSET > vh - VIEWPORT_PAD) side = 'top';
  if (preferredSide === 'left'   && rect.left   - EST_WIDTH  - OFFSET < VIEWPORT_PAD) side = 'right';
  if (preferredSide === 'right'  && rect.right  + EST_WIDTH  + OFFSET > vw - VIEWPORT_PAD) side = 'left';

  /* Raw position before clamping */
  let top  = 0;
  let left = 0;

  switch (side) {
    case 'top':
      top  = rect.top  - OFFSET;
      left = rect.left + rect.width  / 2;
      break;
    case 'bottom':
      top  = rect.bottom + OFFSET;
      left = rect.left + rect.width  / 2;
      break;
    case 'left':
      top  = rect.top  + rect.height / 2;
      left = rect.left - OFFSET;
      break;
    case 'right':
      top  = rect.top  + rect.height / 2;
      left = rect.right + OFFSET;
      break;
  }

  /* Clamp horizontal position so tooltip never goes off-screen */
  if (side === 'top' || side === 'bottom') {
    left = Math.max(EST_WIDTH / 2 + VIEWPORT_PAD, Math.min(left, vw - EST_WIDTH / 2 - VIEWPORT_PAD));
  }

  return { top, left, resolvedSide: side };
}

export function Tooltip({ content, children, side = 'top', className }: TooltipProps) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Position | null>(null);

  const show = useCallback(() => {
    if (!triggerRef.current) return;
    setPos(calcPosition(triggerRef.current.getBoundingClientRect(), side));
  }, [side]);

  const hide = useCallback(() => setPos(null), []);

  return (
    <div
      ref={triggerRef}
      className={cn('inline-flex', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}

      {pos && createPortal(
        <div
          role="tooltip"
          style={{
            position:  'fixed',
            top:       pos.top,
            left:      pos.left,
            transform: TRANSFORM[pos.resolvedSide],
            zIndex:    99999,
            pointerEvents: 'none',
          }}
          className={cn(
            'px-2 py-1 text-xs font-medium text-white whitespace-nowrap rounded-lg',
            'bg-gray-900 dark:bg-dark-700',
            'shadow-[0_4px_16px_rgba(0,0,0,0.25)]',
            'animate-fade-in',
          )}
        >
          {content}
        </div>,
        document.body,
      )}
    </div>
  );
}
